/**
 * Per-metric targets for the Organisation Health "value vs target" bars
 * (replaces the old single-axis radar where every metric shared a flat target
 * of 100 — see BT-110 / BT-224).
 *
 * Each dimension has its own target, unit and direction:
 *  - direction "higher": value should be >= target (e.g. Recovery 80%).
 *  - direction "lower": value should be <= target (e.g. Resting HR 60bpm).
 *
 * Keyed by the dimension label used in the wellbeing_dimensions distribution
 * (case-insensitive), which is the value that flows through to the charts.
 */
export type TargetDirection = 'higher' | 'lower';

export interface MetricTarget {
  target: number;
  unit: string;
  direction: TargetDirection;
}

const METRIC_TARGETS: Record<string, MetricTarget> = {
  recovery: { target: 80, unit: '%', direction: 'higher' },
  'sleep quality': { target: 90, unit: '%', direction: 'higher' },
  consistency: { target: 90, unit: '%', direction: 'higher' },
  'hrv health': { target: 80, unit: 'ms', direction: 'higher' },
  'resting heart rate': { target: 60, unit: 'bpm', direction: 'lower' },
};

/** Returns the target config for a dimension label, or undefined if none. */
export const getMetricTarget = (label: string | undefined): MetricTarget | undefined => {
  if (!label) return undefined;
  return METRIC_TARGETS[label.toLowerCase().trim()];
};

/**
 * Whether a value meets its target given the metric's direction.
 * Returns undefined when there's no target for the label.
 */
export const meetsTarget = (label: string | undefined, value: number): boolean | undefined => {
  const t = getMetricTarget(label);
  if (!t) return undefined;
  return t.direction === 'higher' ? value >= t.target : value <= t.target;
};
